import { ColDef, ICellRendererParams, GridApi } from "ag-grid-community";
import { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import AgGridTable from "../../components/table/AgGridTable";
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Checkbox,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate, useLocation } from "react-router-dom";
import { GridOptions } from "ag-grid-community";
import InviteUserModal from "./InviteUserModal";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "../../axios/axios";
import { useEncryptService } from "../../shared/hooks";
import { toast } from "react-toastify";
import { END_POINTS } from "../../shared/endpoints";
import ConfirmationModal from "../../components/ConfirmationModal";
import down_arrow from "../../assets/down_arrow.svg"
import Tick from "../../assets/Tick.svg"
import Cross from "../../assets/Cross.svg"
import Active from "../../assets/ActiveUser.svg"
import Inactive from "../../assets/InActiveUser.svg"
import useRolePermissions from "../../shared/hooks/useRolePermissions";
import { ROLES_CONTENT_PROPS } from "../../shared/constants/constant-data";
import React from "react";

interface Role {
  id: string;
  name: string;
  status: boolean;
  default_role: boolean;
}

interface UserData {
  id: string;
  position: number;
  firstname: string;
  lastname: string;
  organization_email: string;
  phone_number: string;
  country_code: string;
  is_active: boolean;
  invited: boolean;
  invite_status: string;
  mail_verified: boolean;
  role: string;
  role_id: string;
}

interface APIResponse {
  status: number;
  message: string;
  data: {
    users_list: UserData[];
  };
}

interface RolesAPIResponse {
  status: number;
  message: string;
  data: Role[];
}

// Helper function for status colors
const getStatusColor = (status: string) => {
  switch (status) {
    case "Activated":
      return {
        color: "#00a61b",
        dotColor: "#00a61b",
        background: "rgba(0, 166, 27, .1)",
      };
    case "Pending":
      return {
        color: "#f53b3b",
        dotColor: "#f53b3b",
        background: "rgba(245, 59, 59, .1)",
      };
    default:
      return {
        color: "#696595",
        dotColor: "#696595",
        background: "rgba(105, 101, 149, .1)",
      };
  }
};

const UserManagementTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { hasPermissions } = useRolePermissions();
  const [rowData, setRowData] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [selectedRows, setSelectedRows] = useState<UserData[]>([]);
  const [gridData, setGridData] = useState<UserData[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const { doEncrypt } = useEncryptService();
  const [userData, setUserData] = useState('');
  const [confirmationModal, setConfirmationModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    mail?: string; // Optional field for email
    action: string;
    data?: any; // Add this field to store the data
    confirmButtonText?: string;
    confirmButtonColor?: 'primary' | 'secondary' | 'error' | 'success';
  }>({
    open: false,
    title: '',
    message: '',
    action: '',
  });

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const payload = { type: 1 };
      //const jsonData = JSON.stringify(payload);
      //const encryptedData = doEncrypt(jsonData);

      const response = await axiosInstance.post<APIResponse>(
        'authentication/org-users',
        payload
      );

      if (response.data.status === 200) {
        let users = response.data.data.users_list || [];
        const targetId = location.state?.id;
        if (targetId) {
          users = users.filter((user: UserData) => user.id === targetId);
        }
        setRowData(users);
        setGridData(users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const payload = { type: 1 };
      const jsonData = JSON.stringify(payload);
      const encryptedData = doEncrypt(jsonData);

      const response = await axiosInstance.post<RolesAPIResponse>(
        'authentication/org-roles',
       payload
      );

      if (response.data.status === 200) {
        setRoles(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    const storedUserData = localStorage.getItem("userDetails");
    if (storedUserData) {      
      const parsedUserData = JSON.parse(storedUserData);
      setUserData(parsedUserData?.organization_email);
    }    
  }, [userData, location.state?.id]);

  const handleRoleClick = (event: React.MouseEvent<HTMLElement>, user: UserData) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      left: rect.left,
      top: rect.bottom + window.scrollY,
    });
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
    setSelectedUser(null);
  };

  const handleRoleSelect = (role: Role) => {

    if (selectedUser) {
      updateUserRole(selectedUser, role);
      const updatedData = rowData.map(user =>
        user.id === selectedUser.id
          ? { ...user, role: role.name, role_id: role.id }
          : user
      );

      setRowData(updatedData);
      handleMenuClose();
    }
  };

  const handleActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setAnchorEl(null);
  };

  // const handleAction = async (action: string) => {
  //   switch (action) {
  //     case "delete":
  //       setConfirmationModal((prev) => ({
  //         ...prev,
  //         open: true,
  //         title: 'Deactivate User',
  //         message: 'Are you sure you want to deactivate this user?',
  //         action: 'deactivate',
  //         confirmButtonText: 'Deactivate',
  //         confirmButtonColor: 'error',
  //       }));
  //       break;
  //     case "deactivate":
  //       setConfirmationModal({
  //         open: true,
  //         title: "Deactivate User",
  //         message: `Are you sure you want to deactivate ${selectedUser?.firstname} ${selectedUser?.lastname}?`,
  //         action: "deactivate",
  //         confirmButtonText: "Deactivate",
  //         confirmButtonColor: "error",
  //       });
  //       break;
  //     case "activate":
  //       setConfirmationModal({
  //         open: true,
  //         title: "Activate User",
  //         message: `Are you sure you want to activate ${selectedUser?.firstname} ${selectedUser?.lastname}?`,
  //         action: "activate",
  //         confirmButtonText: "Activate",
  //         confirmButtonColor: "success",
  //       });
  //       break;
  //     case "resendPassword":
  //       setConfirmationModal({
  //         open: true,
  //         title: "Resend Password Link",
  //         message: `Are you sure you want to send a password reset link to ${selectedUser?.organization_email}?`,
  //         action: "resendPassword",
  //         confirmButtonText: "Send Link",
  //       });
  //       break;
  //   }
  //   handleActionsClose();
  // };

  const handleConfirmAction = async () => {
    const { action } = confirmationModal;
    const { data } = confirmationModal; // Get the data from the modal state


    try {
      switch (action) {
        case "delete":
          deleteUser(data); // Pass the data to



          deleteUser(selectedUser!);
          break;
        case "deactivate":
          deactivateUser(data); // Pass the data to the function

          break;
        case "activate":
          activateUser(data); // Pass the data to the function

          break;
        case "reactivate":
          activateUser(data);
          break; // Pass the data to the function
        case "resendPassword":
          resendPasswordLink(data); // Pass the data to the function

          break;
        default:

      }
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
    } finally {
      setConfirmationModal((prev) => ({ ...prev, open: false }));
    }
  };

  const handleBulkAction = (action: string) => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one user");
      handleActionsClose();
      return;
    }

    switch (action) {
      case "delete":
        
        break;
      case "deactivate":
        handleBulkDeactivate();

        break;
      case "resendPassword":

        break;
    }
    handleActionsClose();
  };

  const handleBulkDeactivate = async () => {
    try {
      setIsLoading(true);
      const payload = {
        user_id: selectedRows.map(user => user.id),
        is_active: false

      };
      const response = await axiosInstance.post(
        'authentication/user-activation',
        payload
      );

      if (response.data.status === 200) {
        toast.success("Selected users deactivated successfully");
        fetchUsers();
        setSelectedRows([]);
      }
    } catch (error: any) {
      console.error("Error deleting users:", error);
      toast.error(error.response?.data?.message || "Failed to delete users");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectionChange = (selectedRows: UserData[]) => {
    setSelectedRows(selectedRows);
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };
  const deleteUser = async (params: any) => {

    try {
      const payload = {
        user_id: [params.id]
      };
      const response = await axiosInstance.post(END_POINTS.DELETE_USER, payload);
      if (response.data.status === 200) {
        toast.success("User deleted successfully");
        fetchUsers(); // Refresh the user list after successful deletion
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };
  const deactivateUser = async (params: any) => {

    try {
      const payload = {
        user_id: [params.id],
        is_active: false
      };
      const response = await axiosInstance.post(END_POINTS.DEACTIVATE_USER, payload);
      if (response.data.status === 200) {
        toast.success("User deactivated successfully");

        fetchUsers(); // Refresh the user list after successful deletion
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };
  const activateUser = async (params: any) => {

    try {
      const payload = {
        user_id: [params.id],
        is_active: true
      };
      const response = await axiosInstance.post(END_POINTS.ACTIVATE_USER, payload);
      if (response.data.status === 200) {
        toast.success("User activated successfully");
        fetchUsers(); // Refresh the user list after successful deletion
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const updateUserRole = async (params: any, role: any) => {

    try {
      const payload = {
        role_id: role.id,
        user_id: params.id
      };
      const response = await axiosInstance.post(END_POINTS.CHANGE_ROLE, payload);
      if (response.data.status === 200) {

        fetchUsers(); // Refresh the user list after successful deletion
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };


  const resendPasswordLink = async (params: any) => {

    try {
      const payload = {

        "email": params.organization_email,

      };
      const response = await axiosInstance.post(END_POINTS.RESET, payload);
      if (response.data.status === 200) {

        fetchUsers(); // Refresh the user list after successful deletion
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const columnDefs: ColDef[] = [
    {
      field: "selection",
      headerName: "#",
      maxWidth: 50,
     // checkboxSelection: true,
      //headerCheckboxSelection: true,
      // pinned: "left",
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      lockPosition: true,
      lockPinned: true,
      cellClass: "checkbox-cell",
      headerClass: "checkbox-header",
      showDisabledCheckboxes: true,
      suppressSizeToFit: true,
       checkboxSelection: (params) => {
        return userData &&
        params.data.organization_email &&
        userData !== params.data.organization_email;
      },

    },
    {
      field: "firstname",
      headerName: "First Name",
      filter: forwardRef<any, any>((params, ref) => {
        const [selected, setSelected] = useState<string[]>([]);
        const [isAll, setIsAll] = useState<boolean>(false);
        const [searchText, setSearchText] = useState<string>("");
        const options = useMemo(() => (Array.isArray(params.nameOptions) ? params.nameOptions : []), [params.nameOptions]);

        const filtered = useMemo(() => {
          if (!searchText.trim()) return options;
          return options.filter((o) => o.toLowerCase().includes(searchText.toLowerCase()));
        }, [options, searchText]);

        useImperativeHandle(ref, () => ({
          isFilterActive: () => selected.length > 0 && !isAll,
          doesFilterPass: (nodeParams: any) => {
            if (selected.length === 0 || isAll) return true;
            const val = nodeParams.data?.firstname;
            return selected.includes(String(val));
          },
          getModel: () => (selected.length > 0 && !isAll ? { selectedTypes: selected } : null),
          setModel: (model: any) => {
            if (model && model.selectedTypes) {
              setSelected(model.selectedTypes);
              setIsAll(false);
            } else {
              setSelected([]);
              setIsAll(true);
            }
          },
          getGui: () => null,
        }));

        const toggle = (opt: string) => {
          if (opt === "All") {
            setIsAll(true);
            setSelected([]);
          } else {
            setIsAll(false);
            setSelected((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));
          }
          // @ts-ignore
          if (typeof (params as any).filterModifiedCallback === 'function') (params as any).filterModifiedCallback();
        };

        const apply = () => {
          console.log('FirstNameFilter Apply clicked', { selected, isAll });
          try {
            if (typeof (params as any).onApplyFirstNameFilter === 'function') {
              (params as any).onApplyFirstNameFilter(selected, isAll);
            }
          } catch {}
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        const clear = () => {
          setSelected([]);
          setIsAll(true);
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        return (
          <div style={{ padding: 12, minWidth: 260, backgroundColor: isDarkMode ? '#2d3748' : '#ffffff', border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`, borderRadius: 6, boxShadow: isDarkMode ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: 12, fontWeight: 600, color: isDarkMode ? '#f7fafc' : '#2d3748' }}>Filter First Name</div>
            <div style={{ marginBottom: 12 }}>
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  borderRadius: 4,
                  backgroundColor: isDarkMode ? '#1a202c' : '#ffffff',
                  color: isDarkMode ? '#f7fafc' : '#2d3748',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div className="scroll" style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: isAll ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4 }}>
                <Checkbox size="small" checked={isAll} onChange={() => toggle('All')} />
                <span style={{ marginLeft: 8 }}>All</span>
              </div>
              {filtered.map((opt) => {
                const checked = !isAll && selected.includes(opt);
                return (
                  <div key={opt} style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: checked ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4, marginTop: 2 }}>
                    <Checkbox size="small" checked={checked} onChange={() => toggle(opt)} />
                    <span style={{ marginLeft: 8 }}>{opt}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={clear} style={{ padding: '6px 12px', backgroundColor: isDarkMode ? '#4a5568' : '#e2e8f0', color: isDarkMode ? '#f7fafc' : '#2d3748', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Clear</button>
              <button onClick={apply} style={{ padding: '6px 12px', backgroundColor: '#0969da', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Apply</button>
            </div>
          </div>
        );
      }),
      filterParams: {
        nameOptions: useMemo(() => {
          const s = new Set<string>();
          rowData.forEach((r) => r.firstname && s.add(String(r.firstname)));
          return Array.from(s);
        }, [rowData]),
        onApplyFirstNameFilter: (selected: string[], isAll: boolean) => {
          if (isAll || !selected || selected.length === 0) {
            setGridData(rowData);
            return;
          }
          const targets = selected.map((s) => String(s));
          const filtered = rowData.filter((r) => targets.includes(String(r.firstname)));
          setGridData(filtered);
        },
      },
      // pinned: "left",
      minWidth: 150,
    },
    {
      field: "lastname",
      headerName: "Last Name",
      filter: forwardRef<any, any>((params, ref) => {
        const [selected, setSelected] = useState<string[]>([]);
        const [isAll, setIsAll] = useState<boolean>(false);
        const [searchText, setSearchText] = useState<string>("");
        const options = useMemo(() => (Array.isArray(params.nameOptions) ? params.nameOptions : []), [params.nameOptions]);

        const filtered = useMemo(() => {
          if (!searchText.trim()) return options;
          return options.filter((o) => o.toLowerCase().includes(searchText.toLowerCase()));
        }, [options, searchText]);

        useImperativeHandle(ref, () => ({
          isFilterActive: () => selected.length > 0 && !isAll,
          doesFilterPass: (nodeParams: any) => {
            if (selected.length === 0 || isAll) return true;
            const val = nodeParams.data?.lastname;
            return selected.includes(String(val));
          },
          getModel: () => (selected.length > 0 && !isAll ? { selectedTypes: selected } : null),
          setModel: (model: any) => {
            if (model && model.selectedTypes) {
              setSelected(model.selectedTypes);
              setIsAll(false);
            } else {
              setSelected([]);
              setIsAll(true);
            }
          },
          getGui: () => null,
        }));

        const toggle = (opt: string) => {
          if (opt === 'All') {
            setIsAll(true);
            setSelected([]);
          } else {
            setIsAll(false);
            setSelected((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));
          }
          // @ts-ignore
          if (typeof (params as any).filterModifiedCallback === 'function') (params as any).filterModifiedCallback();
        };

        const apply = () => {
          console.log('LastNameFilter Apply clicked', { selected, isAll });
          try {
            if (typeof (params as any).onApplyLastNameFilter === 'function') {
              (params as any).onApplyLastNameFilter(selected, isAll);
            }
          } catch {}
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        const clear = () => {
          setSelected([]);
          setIsAll(true);
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        return (
          <div  style={{ padding: 12, minWidth: 260, backgroundColor: isDarkMode ? '#2d3748' : '#ffffff', border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`, borderRadius: 6, boxShadow: isDarkMode ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: 12, fontWeight: 600, color: isDarkMode ? '#f7fafc' : '#2d3748' }}>Filter Last Name</div>
            <div style={{ marginBottom: 12 }}>
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  borderRadius: 4,
                  backgroundColor: isDarkMode ? '#1a202c' : '#ffffff',
                  color: isDarkMode ? '#f7fafc' : '#2d3748',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div className="scroll" style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: isAll ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4 }}>
                <Checkbox size="small" checked={isAll} onChange={() => toggle('All')} />
                <span style={{ marginLeft: 8 }}>All</span>
              </div>
              {filtered.map((opt) => {
                const checked = !isAll && selected.includes(opt);
                return (
                  <div key={opt} style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: checked ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4, marginTop: 2 }}>
                    <Checkbox size="small" checked={checked} onChange={() => toggle(opt)} />
                    <span style={{ marginLeft: 8 }}>{opt}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={clear} style={{ padding: '6px 12px', backgroundColor: isDarkMode ? '#4a5568' : '#e2e8f0', color: isDarkMode ? '#f7fafc' : '#2d3748', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Clear</button>
              <button onClick={apply} style={{ padding: '6px 12px', backgroundColor: '#0969da', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Apply</button>
            </div>
          </div>
        );
      }),
      filterParams: {
        nameOptions: useMemo(() => {
          const s = new Set<string>();
          rowData.forEach((r) => r.lastname && s.add(String(r.lastname)));
          return Array.from(s);
        }, [rowData]),
        onApplyLastNameFilter: (selected: string[], isAll: boolean) => {
          if (isAll || !selected || selected.length === 0) {
            setGridData(rowData);
            return;
          }
          const targets = selected.map((s) => String(s));
          const filtered = rowData.filter((r) => targets.includes(String(r.lastname)));
          setGridData(filtered);
        },
      },
      minWidth: 150,
    },
    {
      field: "organization_email",
      headerName: "Email",
      filter: forwardRef<any, any>((params, ref) => {
        const [selected, setSelected] = useState<string[]>([]);
        const [isAll, setIsAll] = useState<boolean>(false);
        const [searchText, setSearchText] = useState<string>("");
        const options = useMemo(() => (Array.isArray(params.emailOptions) ? params.emailOptions : []), [params.emailOptions]);

        const filtered = useMemo(() => {
          if (!searchText.trim()) return options;
          return options.filter((o) => o.toLowerCase().includes(searchText.toLowerCase()));
        }, [options, searchText]);

        useImperativeHandle(ref, () => ({
          isFilterActive: () => selected.length > 0 && !isAll,
          doesFilterPass: (nodeParams: any) => {
            if (selected.length === 0 || isAll) return true;
            const val = nodeParams.data?.organization_email;
            return selected.includes(String(val));
          },
          getModel: () => (selected.length > 0 && !isAll ? { selectedTypes: selected } : null),
          setModel: (model: any) => {
            if (model && model.selectedTypes) {
              setSelected(model.selectedTypes);
              setIsAll(false);
            } else {
              setSelected([]);
              setIsAll(true);
            }
          },
          getGui: () => null,
        }));

        const toggle = (opt: string) => {
          if (opt === 'All') {
            setIsAll(true);
            setSelected([]);
          } else {
            setIsAll(false);
            setSelected((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));
          }
          // @ts-ignore
          if (typeof (params as any).filterModifiedCallback === 'function') (params as any).filterModifiedCallback();
        };

        const apply = () => {
          console.log('EmailFilter Apply clicked', { selected, isAll });
          try {
            if (typeof (params as any).onApplyEmailFilter === 'function') {
              (params as any).onApplyEmailFilter(selected, isAll);
            }
          } catch {}
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        const clear = () => {
          setSelected([]);
          setIsAll(true);
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        return (
          <div style={{ padding: 12, minWidth: 300, backgroundColor: isDarkMode ? '#2d3748' : '#ffffff', border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`, borderRadius: 6, boxShadow: isDarkMode ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: 12, fontWeight: 600, color: isDarkMode ? '#f7fafc' : '#2d3748' }}>Filter Email</div>
            <div style={{ marginBottom: 12 }}>
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  borderRadius: 4,
                  backgroundColor: isDarkMode ? '#1a202c' : '#ffffff',
                  color: isDarkMode ? '#f7fafc' : '#2d3748',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div className="scroll" style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: isAll ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4 }}>
                <Checkbox size="small" checked={isAll} onChange={() => toggle('All')} />
                <span style={{ marginLeft: 8 }}>All</span>
              </div>
              {filtered.map((opt) => {
                const checked = !isAll && selected.includes(opt);
                return (
                  <div key={opt} style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: checked ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4, marginTop: 2 }}>
                    <Checkbox size="small" checked={checked} onChange={() => toggle(opt)} />
                    <span style={{ marginLeft: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={clear} style={{ padding: '6px 12px', backgroundColor: isDarkMode ? '#4a5568' : '#e2e8f0', color: isDarkMode ? '#f7fafc' : '#2d3748', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Clear</button>
              <button onClick={apply} style={{ padding: '6px 12px', backgroundColor: '#0969da', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Apply</button>
            </div>
          </div>
        );
      }),
      filterParams: {
        emailOptions: useMemo(() => {
          const s = new Set<string>();
          rowData.forEach((r) => r.organization_email && s.add(String(r.organization_email)));
          return Array.from(s);
        }, [rowData]),
        onApplyEmailFilter: (selected: string[], isAll: boolean) => {
          if (isAll || !selected || selected.length === 0) {
            setGridData(rowData);
            return;
          }
          const targets = selected.map((s) => String(s));
          const filtered = rowData.filter((r) => targets.includes(String(r.organization_email)));
          setGridData(filtered);
        },
      },
      minWidth: 200
    },
    {
      field: "phone_number",
      headerName: "Phone",
      filter: forwardRef<any, any>((params, ref) => {
        const [selected, setSelected] = useState<string[]>([]);
        const [isAll, setIsAll] = useState<boolean>(false);
        const [searchText, setSearchText] = useState<string>("");
        const options = useMemo(() => (Array.isArray(params.phoneOptions) ? params.phoneOptions : []), [params.phoneOptions]);

        const filtered = useMemo(() => {
          if (!searchText.trim()) return options;
          return options.filter((o) => o.toLowerCase().includes(searchText.toLowerCase()));
        }, [options, searchText]);

        useImperativeHandle(ref, () => ({
          isFilterActive: () => selected.length > 0 && !isAll,
          doesFilterPass: (nodeParams: any) => {
            if (selected.length === 0 || isAll) return true;
            const combined = `${nodeParams.data?.country_code ?? ''} ${nodeParams.data?.phone_number ?? ''}`.trim();
            return selected.includes(String(combined));
          },
          getModel: () => (selected.length > 0 && !isAll ? { selectedTypes: selected } : null),
          setModel: (model: any) => {
            if (model && model.selectedTypes) {
              setSelected(model.selectedTypes);
              setIsAll(false);
            } else {
              setSelected([]);
              setIsAll(true);
            }
          },
          getGui: () => null,
        }));

        const toggle = (opt: string) => {
          if (opt === 'All') {
            setIsAll(true);
            setSelected([]);
          } else {
            setIsAll(false);
            setSelected((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));
          }
          // @ts-ignore
          if (typeof (params as any).filterModifiedCallback === 'function') (params as any).filterModifiedCallback();
        };

        const apply = () => {
          console.log('PhoneFilter Apply clicked', { selected, isAll });
          try {
            if (typeof (params as any).onApplyPhoneFilter === 'function') {
              (params as any).onApplyPhoneFilter(selected, isAll);
            }
          } catch {}
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        const clear = () => {
          setSelected([]);
          setIsAll(true);
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        return (
          <div style={{ padding: 12, minWidth: 260, backgroundColor: isDarkMode ? '#2d3748' : '#ffffff', border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`, borderRadius: 6, boxShadow: isDarkMode ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: 12, fontWeight: 600, color: isDarkMode ? '#f7fafc' : '#2d3748' }}>Filter Phone</div>
            <div style={{ marginBottom: 12 }}>
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  borderRadius: 4,
                  backgroundColor: isDarkMode ? '#1a202c' : '#ffffff',
                  color: isDarkMode ? '#f7fafc' : '#2d3748',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: isAll ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4 }}>
                <Checkbox size="small" checked={isAll} onChange={() => toggle('All')} />
                <span style={{ marginLeft: 8 }}>All</span>
              </div>
              {filtered.map((opt) => {
                const checked = !isAll && selected.includes(opt);
                return (
                  <div key={opt} style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: checked ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4, marginTop: 2 }}>
                    <Checkbox size="small" checked={checked} onChange={() => toggle(opt)} />
                    <span style={{ marginLeft: 8 }}>{opt}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={clear} style={{ padding: '6px 12px', backgroundColor: isDarkMode ? '#4a5568' : '#e2e8f0', color: isDarkMode ? '#f7fafc' : '#2d3748', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Clear</button>
              <button onClick={apply} style={{ padding: '6px 12px', backgroundColor: '#0969da', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Apply</button>
            </div>
          </div>
        );
      }),
      filterParams: {
        phoneOptions: useMemo(() => {
          const s = new Set<string>();
          rowData.forEach((r) => {
            const combined = `${r.country_code ?? ''} ${r.phone_number ?? ''}`.trim();
            if (combined) s.add(combined);
          });
          return Array.from(s);
        }, [rowData]),
        onApplyPhoneFilter: (selected: string[], isAll: boolean) => {
          if (isAll || !selected || selected.length === 0) {
            setGridData(rowData);
            return;
          }
          const targets = selected.map((s) => String(s));
          const filtered = rowData.filter((r) => {
            const combined = `${r.country_code ?? ''} ${r.phone_number ?? ''}`.trim();
            return targets.includes(combined);
          });
          setGridData(filtered);
        },
      },
      minWidth: 150,
      valueGetter: (params) => {
        return params.data.country_code + " " + params.data.phone_number;
      }
    },
    // {
    //   field: "mfa",
    //   headerName: "MFA",
    //   filter: true,
    //   minWidth: 100,
    //   cellRenderer: (params: ICellRendererParams) => {
    //     return (
    //       // <img
    //       //   src={params.value ? Tick : Cross}
    //       //   alt={params.value ? "Verified" : "Not Verified"}
    //       //   style={{ width: 20, height: 20, display: "block", margin: "0 auto" }}
    //       // />
    //       <img
    //         src={Cross}
    //         alt={"Not Verified"}
    //         style={{ width: 20, height: 20, display: "block", margin: "0 auto" }}
    //       />
    //     );
    //   },
    //     cellStyle: {
    //     display: "flex",
    //     alignItems: "center",
    //   },
    // },
    {
      field: "invite_status",
      headerName: "Status",
      filter: forwardRef<any, any>((params, ref) => {
        const [selected, setSelected] = useState<string[]>([]);
        const [isAll, setIsAll] = useState<boolean>(false);
        const [searchText, setSearchText] = useState<string>("");
        const options = useMemo(() => (Array.isArray(params.statusOptions) ? params.statusOptions : []), [params.statusOptions]);

        const filtered = useMemo(() => {
          if (!searchText.trim()) return options;
          return options.filter((o) => o.toLowerCase().includes(searchText.toLowerCase()));
        }, [options, searchText]);

        useImperativeHandle(ref, () => ({
          isFilterActive: () => selected.length > 0 && !isAll,
          doesFilterPass: (nodeParams: any) => {
            if (selected.length === 0 || isAll) return true;
            const val = nodeParams.data?.invite_status;
            return selected.includes(String(val));
          },
          getModel: () => (selected.length > 0 && !isAll ? { selectedTypes: selected } : null),
          setModel: (model: any) => {
            if (model && model.selectedTypes) {
              setSelected(model.selectedTypes);
              setIsAll(false);
            } else {
              setSelected([]);
              setIsAll(true);
            }
          },
          getGui: () => null,
        }));

        const toggle = (opt: string) => {
          if (opt === 'All') {
            setIsAll(true);
            setSelected([]);
          } else {
            setIsAll(false);
            setSelected((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));
          }
          // @ts-ignore
          if (typeof (params as any).filterModifiedCallback === 'function') (params as any).filterModifiedCallback();
        };

        const apply = () => {
          console.log('StatusFilter Apply clicked', { selected, isAll });
          try {
            if (typeof (params as any).onApplyStatusFilter === 'function') {
              (params as any).onApplyStatusFilter(selected, isAll);
            }
          } catch {}
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        const clear = () => {
          setSelected([]);
          setIsAll(true);
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        return (
          <div style={{ padding: 12, minWidth: 240, backgroundColor: isDarkMode ? '#2d3748' : '#ffffff', border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`, borderRadius: 6, boxShadow: isDarkMode ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: 12, fontWeight: 600, color: isDarkMode ? '#f7fafc' : '#2d3748' }}>Filter Status</div>
            <div style={{ marginBottom: 12 }}>
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  borderRadius: 4,
                  backgroundColor: isDarkMode ? '#1a202c' : '#ffffff',
                  color: isDarkMode ? '#f7fafc' : '#2d3748',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: isAll ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4 }}>
                <Checkbox size="small" checked={isAll} onChange={() => toggle('All')} />
                <span style={{ marginLeft: 8 }}>All</span>
              </div>
              {filtered.map((opt) => {
                const checked = !isAll && selected.includes(opt);
                return (
                  <div key={opt} style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: checked ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4, marginTop: 2 }}>
                    <Checkbox size="small" checked={checked} onChange={() => toggle(opt)} />
                    <span style={{ marginLeft: 8 }}>{opt}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={clear} style={{ padding: '6px 12px', backgroundColor: isDarkMode ? '#4a5568' : '#e2e8f0', color: isDarkMode ? '#f7fafc' : '#2d3748', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Clear</button>
              <button onClick={apply} style={{ padding: '6px 12px', backgroundColor: '#0969da', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Apply</button>
            </div>
          </div>
        );
      }),
      filterParams: {
        statusOptions: useMemo(() => {
          const s = new Set<string>();
          rowData.forEach((r) => r.invite_status && s.add(String(r.invite_status)));
          return Array.from(s);
        }, [rowData]),
        onApplyStatusFilter: (selected: string[], isAll: boolean) => {
          if (isAll || !selected || selected.length === 0) {
            setGridData(rowData);
            return;
          }
          const targets = selected.map((s) => String(s));
          const filtered = rowData.filter((r) => targets.includes(String(r.invite_status)));
          setGridData(filtered);
        },
      },
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return null;
        // const { color, dotColor, background } = getStatusColor(params.value);

        // return (
        //   <Chip
        //     label={
        //       <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        //         <span
        //           style={{
        //             width: 8,
        //             height: 8,
        //             borderRadius: "50%",
        //             backgroundColor: dotColor,
        //           }}
        //         />
        //         {params.value}
        //       </span>
        //     }
        //     size="small"
        //     variant="outlined"
        //     sx={{
        //       fontWeight: "bold",
        //       color: color,
        //       maxWidth: "fit-content",
        //       display: "flex",
        //       alignItems: "center",
        //       justifyContent: "start",
        //       mt: 1,
        //       backgroundColor: background,
        //     }}
        //   />
        // );

           if (params.value === "Invited") {
          return (
           <span>{params.value}</span>
          );
        }
        if (params.value === "Activated") {
          return (
            <img
              src={Active}
              alt="Active"
              style={{ width: 20, height: 20, display: "block", margin: "0 auto" }}
            />
          );
        }
        if (params.value === "Deactive") {
          return (
            <img
              src={Inactive}
              alt="Inactive"
              style={{ width: 20, height: 20, display: "block", margin: "0 auto" }}
            />
          );
        }
        return null;
        
      },
        cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  
    {
      field: "role",
      headerName: "Role",
      filter: forwardRef<any, any>((params, ref) => {
        const [selected, setSelected] = useState<string[]>([]);
        const [isAll, setIsAll] = useState<boolean>(false);
        const [searchText, setSearchText] = useState<string>("");
        const options = useMemo(() => (Array.isArray(params.roleOptions) ? params.roleOptions : []), [params.roleOptions]);

        const filtered = useMemo(() => {
          if (!searchText.trim()) return options;
          return options.filter((o) => o.toLowerCase().includes(searchText.toLowerCase()));
        }, [options, searchText]);

        useImperativeHandle(ref, () => ({
          isFilterActive: () => selected.length > 0 && !isAll,
          doesFilterPass: (nodeParams: any) => {
            if (selected.length === 0 || isAll) return true;
            const val = nodeParams.data?.role;
            return selected.includes(String(val));
          },
          getModel: () => (selected.length > 0 && !isAll ? { selectedTypes: selected } : null),
          setModel: (model: any) => {
            if (model && model.selectedTypes) {
              setSelected(model.selectedTypes);
              setIsAll(false);
            } else {
              setSelected([]);
              setIsAll(true);
            }
          },
          getGui: () => null,
        }));

        const toggle = (opt: string) => {
          if (opt === 'All') {
            setIsAll(true);
            setSelected([]);
          } else {
            setIsAll(false);
            setSelected((prev) => (prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]));
          }
          // @ts-ignore
          if (typeof (params as any).filterModifiedCallback === 'function') (params as any).filterModifiedCallback();
        };

        const apply = () => {
          console.log('RoleFilter Apply clicked', { selected, isAll });
          try {
            if (typeof (params as any).onApplyRoleFilter === 'function') {
              (params as any).onApplyRoleFilter(selected, isAll);
            }
          } catch {}
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        const clear = () => {
          setSelected([]);
          setIsAll(true);
          // @ts-ignore
          if (typeof (params as any).filterChangedCallback === 'function') (params as any).filterChangedCallback();
          else params.api?.onFilterChanged?.();
          // @ts-ignore
          params.api?.hidePopupMenu?.();
        };

        return (
          <div style={{ padding: 12, minWidth: 260, backgroundColor: isDarkMode ? '#2d3748' : '#ffffff', border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`, borderRadius: 6, boxShadow: isDarkMode ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: 12, fontWeight: 600, color: isDarkMode ? '#f7fafc' : '#2d3748' }}>Filter Role</div>
            <div style={{ marginBottom: 12 }}>
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  borderRadius: 4,
                  backgroundColor: isDarkMode ? '#1a202c' : '#ffffff',
                  color: isDarkMode ? '#f7fafc' : '#2d3748',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: isAll ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4 }}>
                <Checkbox size="small" checked={isAll} onChange={() => toggle('All')} />
                <span style={{ marginLeft: 8 }}>All</span>
              </div>
              {filtered.map((opt) => {
                const checked = !isAll && selected.includes(opt);
                return (
                  <div key={opt} style={{ display: 'flex', alignItems: 'center', padding: '1px 8px', backgroundColor: checked ? (isDarkMode ? '#3182ce' : '#e2e8f0') : 'transparent', borderRadius: 4, marginTop: 2 }}>
                    <Checkbox size="small" checked={checked} onChange={() => toggle(opt)} />
                    <span style={{ marginLeft: 8 }}>{opt}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={clear} style={{ padding: '6px 12px', backgroundColor: isDarkMode ? '#4a5568' : '#e2e8f0', color: isDarkMode ? '#f7fafc' : '#2d3748', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Clear</button>
              <button onClick={apply} style={{ padding: '6px 12px', backgroundColor: '#0969da', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Apply</button>
            </div>
          </div>
        );
      }),
      filterParams: {
        roleOptions: useMemo(() => {
          const s = new Set<string>();
          rowData.forEach((r) => r.role && s.add(String(r.role)));
          return Array.from(s);
        }, [rowData]),
        onApplyRoleFilter: (selected: string[], isAll: boolean) => {
          if (isAll || !selected || selected.length === 0) {
            setGridData(rowData);
            return;
          }
          const targets = selected.map((s) => String(s));
          const filtered = rowData.filter((r) => targets.includes(String(r.role)));
          setGridData(filtered);
        },
      },
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams) => {
        if (userData === params.data.organization_email) {
          return (
            <Typography variant="body2" sx={{ opacity: 1,display: "inline-flex",  alignItems: "center", gap: "4px",cursor: "default", }}>
              {params.value}
            </Typography>
          ); 
        }
    
        // Render the dropdown if the condition is not met
        if (!params.value) return null;
        return (
          <Box
            onClick={(e) => handleRoleClick(e, params.data)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
            }}
          >
            <Typography variant="body2">{params.value}</Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                opacity: 0.8,
                lineHeight: 1,
              }}
            >
              <img src={down_arrow} alt="down arrow" style={{ width: "12px", height: "12px" }} />
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filter: false,
         cellStyle:()=>{
        return {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
        };  
      },
      cellRenderer: (params: ICellRendererParams) => {
        if (userData === params.data.organization_email || params.data.invite_status === "Invited") {
          return null; // Hide the kebab menu
        }
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const [isLoading, setIsLoading] = useState(false);
        const open = Boolean(anchorEl);

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
          setAnchorEl(null);
        };

        const handleAction = async (action: string, data: any) => {
          switch (action) {
            case "delete":
              setConfirmationModal({
                open: true,
                title: 'Delete User',
                message: `Are you sure you want to delete this user`,
                mail:`${data.organization_email} ?`,
                action: 'delete',
                data, // Pass the data here
                confirmButtonText: 'Delete',
                confirmButtonColor: 'error',
              });
              break;
            case "activate":
              setConfirmationModal({
                open: true,
                title: 'Activate User',
                message: `Are you sure you want to activate this user`,
                mail:`${data.organization_email} ?`,
                action: 'activate',
                data, // Pass the data here
                confirmButtonText: 'Activate',
                confirmButtonColor: 'success',
              });
              break;
            case "deactivate":
              setConfirmationModal({
                open: true,
                title: 'Deactivate User',
                message: `Are you sure you want to deactivate this user`,
                mail:`${data.organization_email} ?`,
                action: 'deactivate',
                data, // Pass the data here
                confirmButtonText: 'Deactivate',
                confirmButtonColor: 'error',
              });
              break;
            case "reactivate":
              setConfirmationModal({
                open: true,
                title: 'Reactivate User',
                message: `Are you sure you want to reactivate this user`,
                mail:`${data.organization_email} ?`,
                action: 'deactivate',
                data, // Pass the data here
                confirmButtonText: 'Reactivate',
                confirmButtonColor: 'error',
              });
              break;
            case "resendPassword":
              setConfirmationModal({
                open: true,
                title: 'Resend Password Link',
                message: `Are you sure you want to send a password reset link to`,
                mail:`${data.organization_email} ?`,
                action: 'resendPassword',
                data, // Pass the data here
                confirmButtonText: 'Resend Link',
                confirmButtonColor: 'primary',
              });
              break;
          }
          handleActionsClose();
        };

        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <IconButton
              size="small"
              onClick={handleClick}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              onClick={(e) => e.stopPropagation()}
              PaperProps={{
                sx: {
                  width: 200,
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              {/* {params.data.invite_status === "Invited" ? (
                // Show only the "Delete" option if invite_status is "Invited"
                hasPermissions(ROLES_CONTENT_PROPS.DELETE_USER) && (
                  <MenuItem
                  onClick={() => handleAction("delete", params.data)}
                  sx={{
                    color: "error.main",
                    "&:hover": {
                      backgroundColor: "error.lighter",
                    },
                  }}
                >
                  Delete
                </MenuItem>
                )
              ) : (
                
                <>
                  {hasPermissions(ROLES_CONTENT_PROPS.DELETE_USER) && (
                    <MenuItem
                    onClick={() => handleAction("delete", params.data)}
                    sx={{
                      color: "error.main",
                      "&:hover": {
                        backgroundColor: "error.lighter",
                      },
                    }}
                  >
                    Delete
                  </MenuItem>
                  )}

                  {hasPermissions(ROLES_CONTENT_PROPS.ACTIVATE_DEACTIVATE) && (
                    <MenuItem
                    onClick={() =>
                      handleAction(params.data.is_active ? "deactivate" : "activate", params.data)
                    }
                  >
                    {params.data.is_active ? "Deactivate" : "Activate"}
                  </MenuItem>
                  )}
                  {hasPermissions(ROLES_CONTENT_PROPS.RESET_PASS_LINK) && (
                    <MenuItem
                    onClick={() => handleAction("resendPassword", params.data)}
                    disabled={isLoading}
                  >
                    {isLoading ? "Sending..." : "Resend Password Link"}
                  </MenuItem>
                  )}

                </>
              )} */}
           
              
                  {hasPermissions(ROLES_CONTENT_PROPS.ACTIVATE_DEACTIVATE) && (
                    <MenuItem
                    onClick={() =>
                      handleAction(params.data.is_active ? "deactivate" : "activate", params.data)
                    }
                  >
                    {params.data.is_active ? "Deactivate" : "Activate"}
                  </MenuItem>
                  )}
                  {hasPermissions(ROLES_CONTENT_PROPS.RESET_PASS_LINK) && (
                    <MenuItem
                    onClick={() => handleAction("resendPassword", params.data)}
                    disabled={isLoading}
                  >
                    {isLoading ? "Sending..." : "Resend Password Link"}
                  </MenuItem>
                  )}
                
            </Menu>
          </Box>
        );
      },
    },
  ];

    const baseColumnDefs: ColDef[] = hasPermissions(ROLES_CONTENT_PROPS.DELETE_USER)
    ? columnDefs
    : columnDefs.filter(col => col.field !== "selection");

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressMovable: false,
  };

  const gridOptions: GridOptions = {
    defaultColDef,
    rowHeight: 48,
    headerHeight: 48,
    suppressCellFocus: true,
    rowStyle: { cursor: "default" },
    suppressRowClickSelection: true,
    rowMultiSelectWithClick: false,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    rowSelection: "multiple" as const,
    getRowId: (params) => params.data.id,
    onSelectionChanged: (event: any) => {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node: any) => node.data);
      setSelectedRows(selectedData);
    },
    onGridReady: onGridReady
  };

  return (
    <Card
      variant="outlined"
      sx={{ height: "calc(100vh - 120px)", borderRadius: 3 }}
    >
      <AgGridTable
        columnDefs={baseColumnDefs}
        data={gridData || rowData}
        height={"calc(100vh - 160px)"}
        showSearch
        searchPlaceholder="Search Users"
        headerActions={
            <Box display="flex" alignItems="center" gap={2}>
            {selectedRows.length > 0 ? (
              hasPermissions(ROLES_CONTENT_PROPS.DELETE_USER) && (
              <Button
                variant="outlined"
                sx={{ color: "#F53B3B", borderColor: "#F53B3B", fontWeight: 600, fontSize: "14px" }}
                onClick={() => handleBulkAction("deactivate")}
                // startIcon={<DeleteIcon />}
              >
                Deactivate User{selectedRows.length > 1 ? 's' : ''}
              </Button>
              )
            ) : (
              hasPermissions(ROLES_CONTENT_PROPS.ADD_USER) && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setIsInviteModalOpen(true)}
                // startIcon={<AddIcon />}
              >
                Add User
              </Button>
              )
            )}
            </Box>
        }
        isLoading={isLoading}
        isRowSelectable={true}
        rowSelection="multiple"
        gridOptions={gridOptions}
        onSelectionChange={handleSelectionChange}
      />
      <Menu
        open={Boolean(menuPosition)}
        onClose={handleMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          menuPosition
            ? {
              top: menuPosition.top,
              left: menuPosition.left,
            }
            : undefined
        }
        PaperProps={{
          sx: {
            width: 200,
            mt: 1,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        {roles.filter(role => role.status).map((role) => (
          <MenuItem
            key={role.id}
            onClick={() => handleRoleSelect(role)}
            // selected={selectedUser?.role_id === role.id}
            sx={{
              mx: 1,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              // "&.Mui-selected": {
              //   backgroundColor: "primary.light",
              //   "&:hover": {
              //     backgroundColor: "primary.light",
              //   },
              // },
            }}
          >
            {role.name}
          </MenuItem>
        ))}
      </Menu>
      <InviteUserModal
        open={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInviteSuccess={fetchUsers}
      />
      <ConfirmationModal
        open={confirmationModal.open}
        onClose={() => setConfirmationModal((prev) => ({ ...prev, open: false }))}
        onConfirm={handleConfirmAction}
        title={confirmationModal.title}
        message={confirmationModal.message}
        mail={confirmationModal.mail}
        confirmButtonText={confirmationModal.confirmButtonText}
        confirmButtonColor={confirmationModal.confirmButtonColor}
      />
    </Card>
  );
};

export default UserManagementTable;
